import type { WorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import { whatsappIntegrationDisplayLabel } from "@/lib/whatsapp-integration-label";
import type { SalesFunnel } from "@/types/lead";

export interface FunnelDeletionContext {
  funnelCount: number;
  integrationById?: WorkspaceIntegrationsMap;
}

export interface IntegrationFunnelReference {
  integrationId: string;
  label: string;
}

export function getIntegrationsReferencingFunnel(
  funnelId: string,
  integrationById?: WorkspaceIntegrationsMap
): IntegrationFunnelReference[] {
  if (!integrationById) return [];

  const refs: IntegrationFunnelReference[] = [];
  for (const [integrationId, integration] of integrationById) {
    const cfg = integration?.config as { selected_funnel_id?: string } | undefined;
    const selected =
      cfg && typeof cfg.selected_funnel_id === "string"
        ? cfg.selected_funnel_id
        : null;
    if (selected === funnelId) {
      refs.push({
        integrationId,
        label: whatsappIntegrationDisplayLabel(integration, integrationId.slice(0, 8)),
      });
    }
  }
  return refs;
}

export function getFunnelDeletionBlockReason(
  funnel: SalesFunnel,
  ctx: FunnelDeletionContext
): string | null {
  if (funnel.is_default) {
    return "O funil padrão não pode ser excluído. Defina outro funil como padrão antes.";
  }

  if (ctx.funnelCount <= 1) {
    return "Você precisa ter pelo menos um funil no workspace.";
  }

  if (funnel.integration_id) {
    return "Este funil está vinculado a uma integração e não pode ser excluído. Desconecte a integração primeiro.";
  }

  return null;
}

export function canDeleteFunnel(funnel: SalesFunnel, ctx: FunnelDeletionContext): boolean {
  return getFunnelDeletionBlockReason(funnel, ctx) === null;
}

export function getFunnelDeletionWarning(
  funnel: SalesFunnel,
  ctx: FunnelDeletionContext
): string | null {
  const refs = getIntegrationsReferencingFunnel(funnel.id, ctx.integrationById);
  if (refs.length === 0) return null;

  const names = refs.map((r) => r.label).join(", ");
  if (refs.length === 1) {
    return `A integração "${names}" usa este funil para novos leads. A referência será removida automaticamente.`;
  }
  return `As integrações ${names} usam este funil para novos leads. As referências serão removidas automaticamente.`;
}
